import { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { useDispatch } from 'react-redux';
import { showNotification } from '../../store/Reducers/NotificationSnackbarSlice';
import { OtpProviderService } from '../../api/services/otpProviderService';
import { OtpProviderResponse } from '../../types/api/response/otpproviderresponse';

export const OtpProvidersPage = () => {
  const dispatch = useDispatch();
  const [otpProviders, setOtpProviders] = useState<OtpProviderResponse[]>([]);
  const [loading, setLoading] = useState(false);
  const [activating, setActivating] = useState(false);
  const [confirmActivate, setConfirmActivate] = useState<OtpProviderResponse | null>(null);

  const fetchOtpProviders = useCallback(async () => {
    setLoading(true);
    try {
      const data = await OtpProviderService.listOtpProviders();
      setOtpProviders(data);
    } catch (e: any) {
      dispatch(
        showNotification({
          message: e?.response?.data?.message ?? 'Failed to load OTP providers.',
          severity: 'error',
        }),
      );
    } finally {
      setLoading(false);
    }
  }, [dispatch]);

  useEffect(() => {
    fetchOtpProviders();
  }, [fetchOtpProviders]);

  const handleActivate = async () => {
    if (!confirmActivate) return;
    setActivating(true);
    try {
      const data = await OtpProviderService.activateOtpProvider(confirmActivate.id);
      setOtpProviders(data);
      dispatch(
        showNotification({
          message: `${confirmActivate.provider_key} is now the active OTP provider.`,
          severity: 'success',
        }),
      );
    } catch (e: any) {
      dispatch(
        showNotification({
          message: e?.response?.data?.message ?? 'Failed to switch OTP provider.',
          severity: 'error',
        }),
      );
    } finally {
      setActivating(false);
      setConfirmActivate(null);
    }
  };

  return (
    <Box>
      <Typography variant="body2" color="text.secondary" mb={2}>
        Exactly one provider is active at a time — customer OTP codes are sent through whichever
        provider is Active below.
      </Typography>

      {loading && (
        <Box display="flex" justifyContent="center" py={6}>
          <CircularProgress />
        </Box>
      )}

      {!loading && otpProviders.length > 0 && (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Provider</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Updated At</TableCell>
                <TableCell>Updated By</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {otpProviders.map((item) => (
                <TableRow key={item.id}>
                  <TableCell>
                    <code>{item.provider_key}</code>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={item.active ? 'Active' : 'Inactive'}
                      color={item.active ? 'success' : 'default'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>{item.updated_at ?? '—'}</TableCell>
                  <TableCell>{item.updated_by ?? '—'}</TableCell>
                  <TableCell>
                    <Button
                      size="small"
                      variant="outlined"
                      disabled={item.active}
                      onClick={() => setConfirmActivate(item)}
                    >
                      Activate
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Activate confirmation */}
      <Dialog open={!!confirmActivate} onClose={() => setConfirmActivate(null)}>
        <DialogTitle>Switch OTP Provider</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Activating {confirmActivate?.provider_key} will immediately deactivate the current
            provider. All new customer OTP codes will be sent through{' '}
            {confirmActivate?.provider_key} from this point on. Continue?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmActivate(null)} disabled={activating}>
            Cancel
          </Button>
          <Button
            color="warning"
            variant="contained"
            onClick={handleActivate}
            disabled={activating}
          >
            {activating ? 'Switching...' : 'Activate'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
